import React, { useState } from "react";
import { Smartphone, Download, Share, PlusSquare, Check, ShieldCheck, Zap, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const ICON = "/app-icon-512.png";

export default function AppDownload() {
  const { canInstall, promptInstall, installed, isIOS } = usePwaInstall();
  const [busy, setBusy] = useState(false);

  const onInstall = async () => {
    setBusy(true);
    const outcome = await promptInstall();
    setBusy(false);
    if (outcome === "accepted") toast.success("Installing the HMC app…");
    else if (outcome === "dismissed") toast("Installation cancelled.");
  };

  return (
    <div data-testid="app-download-page">
      <Seo
        title="Get the HMC App — Hussar Marine Concierge"
        description="Install the Hussar Marine Concierge app to your home screen. Your Vessel. Our Vigilance. Wherever You Are."
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden bg-navy-900">
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <img
              src={ICON}
              alt="Hussar Marine Concierge app icon"
              width={132}
              height={132}
              className="mx-auto h-28 w-28 sm:h-32 sm:w-32 rounded-[26px] shadow-[0_24px_60px_-18px_rgba(212,175,55,0.5)] ring-1 ring-gold-500/30"
              data-testid="app-icon-hero"
            />
            <p className="eyebrow text-gold-400 mt-8 mb-4">The HMC App</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.08] text-shadow-navy">
              Your Vessel. Our Vigilance.
              <br className="hidden sm:block" /> Wherever You Are.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Keep Hussar Marine Concierge just one tap away.
            </p>

            <div className="mt-10" data-testid="install-cta">
              {installed ? (
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-300 px-6 py-3 font-semibold"
                  data-testid="app-installed-badge"
                >
                  <Check className="h-5 w-5" /> App installed — you're all set
                </span>
              ) : canInstall ? (
                <button
                  onClick={onInstall}
                  disabled={busy}
                  className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-navy-900 font-semibold text-base px-8 py-4 rounded-full transition-all hover:shadow-[0_8px_28px_-8px_rgba(212,175,55,0.8)]"
                  data-testid="install-app-button"
                >
                  <Download className="h-5 w-5" /> Install HMC App
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 text-slate-200 px-6 py-3"
                  data-testid="install-manual-hint"
                >
                  <Smartphone className="h-5 w-5 text-gold-400" />
                  {isIOS ? "Tap Share, then Add to Home Screen" : "Use your browser menu to install"}
                </span>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Instructions + benefits */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-serif text-2xl text-navy-900 dark:text-white">How to install</h2>
            <div className="mt-6 space-y-6">
              <InstallBlock
                title="iPhone & iPad (Safari)"
                testid="ios-steps"
                icon={Share}
                steps={[
                  "Open HussarMarineConcierge.com in Safari",
                  "Tap the Share button (the square with an upward arrow)",
                  "Choose \u201CAdd to Home Screen\u201D",
                  "Tap \u201CAdd\u201D \u2014 the HMC icon appears on your home screen",
                ]}
              />
              <InstallBlock
                title="Android (Chrome)"
                testid="android-steps"
                icon={Download}
                steps={[
                  "Open the site in Chrome",
                  "Tap \u201CInstall HMC App\u201D above, or open the \u22EE menu",
                  "Choose \u201CInstall app\u201D / \u201CAdd to Home screen\u201D",
                  "Confirm \u2014 HMC installs like a native app",
                ]}
              />
              <InstallBlock
                title="Desktop (Chrome / Edge)"
                testid="desktop-steps"
                icon={PlusSquare}
                steps={[
                  "Open the site in Chrome or Edge",
                  "Click the install icon in the address bar, or open the \u22EE menu",
                  "Choose \u201CInstall Hussar Marine Concierge\u201D",
                ]}
              />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-serif text-2xl text-navy-900 dark:text-white">Why install</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <Benefit icon={Zap} title="One-tap access" body="Launch HMC straight from your home screen." />
              <Benefit icon={Smartphone} title="App-like experience" body="Full-screen and standalone — no browser clutter." />
              <Benefit icon={WifiOff} title="Fast & resilient" body="Core pages load quickly, even on a weak signal." />
              <Benefit icon={ShieldCheck} title="Private by design" body="No tracking, no background location, served over HTTPS." />
            </div>
            <div className="mt-8 rounded-2xl border border-gold-500/25 bg-ivory dark:bg-navy-800 p-6" data-testid="app-security-note">
              <p className="eyebrow text-gold-600 dark:text-gold-400 mb-2">Security</p>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                The HMC app is a secure web app installed to your device. It requests no special permissions, performs no
                background tracking, and never stores your inquiries, login, or any customer data on your device.
              </p>
            </div>
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
              Not available on the Apple App Store or Google Play — it installs directly from the web.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const InstallBlock = ({ title, steps, icon: Icon, testid }) => (
  <div className="rounded-2xl border border-gold-500/20 bg-ivory dark:bg-navy-800 p-6" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-gold-500" />
      </div>
      <h3 className="font-serif text-lg text-navy-900 dark:text-white">{title}</h3>
    </div>
    <ol className="mt-4 space-y-2 list-decimal list-inside text-sm text-slate-600 dark:text-slate-300">
      {steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  </div>
);

const Benefit = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-3">
    <div className="h-10 w-10 rounded-xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-gold-500" />
    </div>
    <div>
      <p className="font-semibold text-navy-900 dark:text-white text-sm">{title}</p>
      <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{body}</p>
    </div>
  </div>
);
